import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import ProgressIndicator from "../ProgressIndicator";
import { WizardStepError } from "../WizardStepError";
import { WizardStepSkeleton } from "../WizardStepSkeleton";

import InvestmentProfileActions from "./components/InvestmentProfileActions";
import InvestmentProfileList from "./components/InvestmentProfileList";
import { useInvestmentProfileManagement } from "./hooks/useInvestmentProfileManagement";
import {
  filterOptionsByAcademicYear,
  getInvestmentOptionDetails,
  getUniqueAcademicYears,
} from "./utils/investmentProfileUtils";

const InvestmentProfile: React.FC<{
  handleNext: () => void;
  handlePrevious: () => void;
  isUserEnrolled?: boolean;
  onCancel?: () => void;
}> = ({ handlePrevious, handleNext, isUserEnrolled, onCancel }) => {
  const { t } = useTranslation();
  const {
    beneficiaries,
    investmentOptionsFromAPI,
    loading,
    isSubmitting,
    error,
    handleInitialDataSync,
    handleCustomPlanSelection,
    handleAcademicYearSelection,
    handleSubmit,
  } = useInvestmentProfileManagement();

  useEffect(() => {
    handleInitialDataSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitWrapper = () => {
    handleSubmit(isUserEnrolled, onCancel, handleNext);
  };

  const hasMichiganOptions = investmentOptionsFromAPI.some(
    (option) => option.state529 === "MICHIGAN",
  );

  const getAcademicYearOptions = (beneficiaryId: string) => {
    // Find the beneficiary to get their birth date
    const beneficiary = beneficiaries.find((b) => b.id === beneficiaryId);
    if (!beneficiary) {
      return getUniqueAcademicYears(investmentOptionsFromAPI);
    }

    // Calculate recommended year based on beneficiary's current academic year
    const recommendedYear = beneficiary.selectedAcademicTargetYear;
    return getUniqueAcademicYears(investmentOptionsFromAPI, recommendedYear);
  };

  const getFilteredInvestmentOptions = (academicYear: string) => {
    const filteredOptions = filterOptionsByAcademicYear(
      investmentOptionsFromAPI,
      academicYear,
    );
    return filteredOptions.map((option) => ({
      label:
        option.benefitType === "Full"
          ? `${option.portfolioName} (Recommended)`
          : option.portfolioName,
      value: option.portfiloShortName,
    }));
  };

  const getSelectedPortfolioDetails = (investmentOptionId: string) => {
    return getInvestmentOptionDetails(
      investmentOptionId,
      investmentOptionsFromAPI,
    );
  };

  return (
    <>
      <div className="w-full mx-auto font-outfit sm:w-[640px]">
        <ProgressIndicator />

        {!isUserEnrolled && hasMichiganOptions && (
          <>
            <p className="mb-6">
              {t("onboarding.investment_profile.description")
                .split("MET 529 program")
                .map((part, index) =>
                  index === 0 ? (
                    <span key={index}>{part}</span>
                  ) : (
                    <span key={index}>
                      <span className="font-semibold">
                        {t("onboarding.investment_profile.met_program_text")}
                      </span>
                      {part}
                    </span>
                  ),
                )}
            </p>

            <ul className="">
              <li className="py-1">
                <a
                  href="https://www.michigan.gov/setwithmet/-/media/Project/Websites/setwithmet/documents/5354_MET_Enrollment_Kit.pdf?rev=a2a74d2784584d88b0ba3c82c8c52bdb&hash=FA22BF3D82488140280AED02E95D506B"
                  target="_blank"
                  className="text-[#00ADAC] underline "
                >
                  {t("onboarding.investment_profile.links.about_met")}
                </a>
              </li>
              <li className="py-1">
                <a
                  href="https://www.michigan.gov/setwithmet/-/media/Project/Websites/setwithmet/documents/Price-Charts.pdf?rev=75e40e4dca934512937140bb391d5dc8"
                  target="_blank"
                  className="text-[#00ADAC] underline "
                >
                  {t("onboarding.investment_profile.links.view_credit_prices")}
                </a>
              </li>
            </ul>
          </>
        )}

        <div className="mt-6">
          {loading ? (
            <WizardStepSkeleton />
          ) : error ? (
            <WizardStepError step={4} handlePrevious={handlePrevious} />
          ) : (
            <>
              <InvestmentProfileList
                beneficiaries={beneficiaries}
                getAcademicYearOptions={getAcademicYearOptions}
                getFilteredInvestmentOptions={getFilteredInvestmentOptions}
                getSelectedPortfolioDetails={getSelectedPortfolioDetails}
                onCustomPlanSelection={handleCustomPlanSelection}
                onAcademicYearSelection={handleAcademicYearSelection}
                isUserEnrolled={isUserEnrolled}
              />

              <InvestmentProfileActions
                isUserEnrolled={isUserEnrolled}
                isSubmitting={isSubmitting}
                onPrevious={handlePrevious}
                onSubmit={handleSubmitWrapper}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default InvestmentProfile;
